import { useEffect, useRef, useState, useCallback } from "react";
import type { MinimalUser } from "@/components/travel/types";
import { useAccessToken } from "@/context/AccessTokenContext";
import type {
  NavigateTo,
  TravelClientConfig,
  TravelClientError,
  TravelClientEvent,
  TravelClientUpdate,
} from "@/components/travel/types";

interface UseTravelSdkProps {
  user: MinimalUser | null;
  containerId: string;
  width?: string | number;
  height?: string | number;
  language?: string;
  navigateTo?: NavigateTo;
  onLanguageChange?: (language: string) => void;
  onNavigate?: (navigation: NavigateTo) => void;
  onError?: (error: TravelClientError) => void;
  onUpdate?: (update: TravelClientUpdate) => void;
}

interface UseTravelSdkReturn {
  status: "idle" | "loading" | "initialized" | "error";
  error: { message: string; isCsp?: boolean } | null;
  sdkError: TravelClientError | null;
  containerRef: React.RefObject<HTMLDivElement>;
  updateLanguage: (newLanguage: string) => void;
  navigateToView: (navigation: NavigateTo) => void;
  refreshSession: () => Promise<boolean>;
}

export const SDK_SCRIPT_URL =
  import.meta.env.VITE_ACCESS_DEV_SDK_URL ||
  "https://booking.accessdevelopment.com/scripts/travel.client.v2.js";

export const prefetchAccessDevSdk = (): void => {
  if (typeof document === "undefined") return;
  if (
    document.querySelector(`link[rel="preload"][href="${SDK_SCRIPT_URL}"]`) ||
    document.querySelector(`script[src="${SDK_SCRIPT_URL}"]`)
  ) {
    return;
  }
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "script";
  link.href = SDK_SCRIPT_URL;
  document.head.appendChild(link);
};

const ACCESS_DEV_SESSION_RECOVERY_MESSAGE =
  "The Access travel session is no longer valid. Reload the page to start a new session.";

const TRAVEL_CLIENT_CSP_HOSTS = [
  "accessdevelopment.com",
  "adcrws.com",
  "adcrws-stage.com",
] as const;

const parseHostname = (uri: string): string | null => {
  if (!uri || uri === "inline" || uri === "eval" || uri === "self") {
    return null;
  }

  try {
    return new URL(uri).hostname.toLowerCase();
  } catch {
    return null;
  }
};

const matchesHostOrSubdomain = (hostname: string, domain: string): boolean =>
  hostname === domain || hostname.endsWith(`.${domain}`);

export const isTravelClientCspViolation = (
  event: Pick<SecurityPolicyViolationEvent, "blockedURI">
): boolean => {
  const hostname = parseHostname(event.blockedURI);

  if (!hostname) {
    return false;
  }

  return TRAVEL_CLIENT_CSP_HOSTS.some((domain) =>
    matchesHostOrSubdomain(hostname, domain)
  );
};

/** Check if the SDK is fully ready (object exists with .start method). */
const isSdkReady = (): boolean =>
  typeof window.travelClient === "object" &&
  window.travelClient !== null &&
  typeof window.travelClient.start === "function";

/**
 * Wait for `window.travelClient.start` to become available.
 * The SDK script may set `window.travelClient = {}` before populating methods.
 */
const waitForSdkReady = (timeoutMs = 5000): Promise<void> =>
  new Promise((resolve, reject) => {
    if (isSdkReady()) {
      resolve();
      return;
    }
    const start = Date.now();
    const interval = setInterval(() => {
      if (isSdkReady()) {
        clearInterval(interval);
        resolve();
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(interval);
        // Log what we actually got for debugging
        console.error(
          "[Access Dev] SDK object after timeout:",
          typeof window.travelClient,
          window.travelClient
            ? Object.keys(window.travelClient)
            : "undefined"
        );
        reject(
          new Error(
            "Access Dev SDK loaded but .start() not available — " +
              "the SDK may have changed its API or failed to initialize"
          )
        );
      }
    }, 100);
  });

/** Dynamically load the Access Dev SDK script (once). */
const loadSdkScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (import.meta.env.DEV) console.info("[Access Dev] Loading SDK from:", SDK_SCRIPT_URL);

    // If the SDK is already fully ready, skip loading
    if (isSdkReady()) {
      if (import.meta.env.DEV) console.info("[Access Dev] SDK already loaded, skipping script injection");
      resolve();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SDK_SCRIPT_URL}"]`
    );

    if (existing) {
      if (import.meta.env.DEV) console.info("[Access Dev] Script tag already exists, waiting for SDK ready");
      waitForSdkReady().then(resolve, reject);
      return;
    }

    if (import.meta.env.DEV) console.info("[Access Dev] Injecting script tag");
    const script = document.createElement("script");
    script.src = SDK_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      // Check if the SDK object exists at all — if not, the server likely
      // returned an error page (e.g. 503) instead of JavaScript
      if (typeof window.travelClient === "undefined") {
        console.error(
          "[Access Dev] Script tag loaded but window.travelClient is undefined.",
          "The SDK server may be down or returned a non-JS response.",
          "URL:", SDK_SCRIPT_URL
        );
        reject(new Error(
          `Access Dev SDK server may be down — loaded ${SDK_SCRIPT_URL} but no SDK object was defined`
        ));
        return;
      }
      if (import.meta.env.DEV) console.info("[Access Dev] Script loaded, waiting for SDK ready");
      waitForSdkReady().then(resolve, reject);
    };
    script.onerror = () => {
      console.error("[Access Dev] Script failed to load:", SDK_SCRIPT_URL);
      reject(new Error("Failed to load Access Dev SDK script — network error or blocked by CSP"));
    };
    document.head.appendChild(script);
  });
};

export const useTravelSdk = ({
  user,
  containerId,
  width,
  height,
  language,
  navigateTo,
  onLanguageChange,
  onNavigate,
  onError,
  onUpdate,
}: UseTravelSdkProps): UseTravelSdkReturn => {
  const { fetchToken, resetTokenState } = useAccessToken();

  const [status, setStatus] = useState<
    "idle" | "loading" | "initialized" | "error"
  >("idle");
  const [error, setError] = useState<{
    message: string;
    isCsp?: boolean;
  } | null>(null);
  const [sdkError, setSdkError] = useState<TravelClientError | null>(null);

  const isInitialized = useRef(false);
  const _containerRef = useRef<HTMLDivElement>(null);
  const containerRef = _containerRef as React.RefObject<HTMLDivElement>;
  const lastScrollPosition = useRef<number>(0);
  const hasAttachedErrorListener = useRef(false);
  const hasAttachedUpdateListener = useRef(false);
  const hasAttachedEventListener = useRef(false);

  // Store callbacks in refs so listeners don't need to re-attach
  const onErrorRef = useRef(onError);
  const onUpdateRef = useRef(onUpdate);
  const onNavigateRef = useRef(onNavigate);
  const onLanguageChangeRef = useRef(onLanguageChange);
  onErrorRef.current = onError;
  onUpdateRef.current = onUpdate;
  onNavigateRef.current = onNavigate;
  onLanguageChangeRef.current = onLanguageChange;

  const markSessionRestartRequired = useCallback(
    (message = ACCESS_DEV_SESSION_RECOVERY_MESSAGE) => {
      resetTokenState();
      setError({ message });
      setStatus("error");
    },
    [resetTokenState]
  );

  const initializeSdk = useCallback(
    async (token: string) => {
      try {
        if (import.meta.env.DEV) console.info("[Access Dev] initializeSdk called, token length:", token?.length ?? 0);
        if (!isInitialized.current) {
          setStatus("loading");
        }

        setSdkError(null);

        // In production, load the SDK script dynamically
        if (!import.meta.env.DEV) {
          await loadSdkScript();
        }

        if (typeof window === "undefined" || !isSdkReady()) {
          const tc = window.travelClient;
          console.error(
            "[Access Dev] Travel Client SDK not available after script load.",
            {
              sdkUrl: SDK_SCRIPT_URL,
              travelClientType: typeof tc,
              keys: tc ? Object.keys(tc) : [],
              startType: tc ? typeof (tc as Record<string, unknown>).start : "n/a",
            }
          );
          throw new Error(
            `Travel Client SDK not loaded — window.travelClient is ${typeof tc}` +
              (tc ? ` with keys: [${Object.keys(tc).join(", ")}]` : "")
          );
        }

        const travelClient = window.travelClient!;

        if (!hasAttachedErrorListener.current) {
          hasAttachedErrorListener.current = true;

          travelClient.on("error", (event: TravelClientError) => {
            setSdkError(event);
            onErrorRef.current?.(event);

            if (
              event.error_code === "MISSING_SESSION_TOKEN" ||
              event.error_code === "INVALID_SESSION_TOKEN"
            ) {
              console.warn(
                `[Access Dev] Session token issue (${event.error_code}): ${event.error_message}.`
              );
              markSessionRestartRequired(
                "Access rejected the current session token. Reload the page to start a new session."
              );
            }
          });
        }

        if (!hasAttachedUpdateListener.current) {
          hasAttachedUpdateListener.current = true;

          travelClient.on("update", (update: TravelClientUpdate) => {
            onUpdateRef.current?.(update);

            if (update.update_code === "TRAVEL_CLIENT_LOADED") {
              if (import.meta.env.DEV) {
                console.info("[Access Dev] Travel client loaded.");
              }
              setStatus("initialized");
              return;
            }

            if (update.update_code === "ACTIVE_SESSION_PING") {
              if (import.meta.env.DEV) {
                console.info("[Access Dev] Active session ping received.");
              }
              return;
            }

            if (
              update.update_code === "SESSION_NOT_FOUND" ||
              update.update_code === "TRAVEL_CLIENT_SESSION_EXPIRED"
            ) {
              console.warn(
                `[Access Dev] ${update.update_code}: ${update.update_message ?? "session expired"}`
              );
              markSessionRestartRequired();
            }
          });
        }

        if (!hasAttachedEventListener.current) {
          hasAttachedEventListener.current = true;

          travelClient.on("event", (event: TravelClientEvent) => {
            if (import.meta.env.DEV) {
              console.info("[Access Dev] Travel client event:", event.event_code);
            }
          });
        }

        if (isInitialized.current) {
          markSessionRestartRequired();
          return false;
        }

        const config: TravelClientConfig = {
          session_token: token,
          container: `#${containerId}`,
        };

        if (width) config.width = width;
        if (height) config.height = height;
        if (language) {
          config.language = language;
          config.languge = language;
        }
        if (navigateTo) config.navigate_to = navigateTo;

        if (import.meta.env.DEV) console.info("[Access Dev] Calling travelClient.start()");
        travelClient.start(config);

        isInitialized.current = true;

        // Check that the production SDK actually rendered an iframe in the
        // container. Skipped in dev: the dummy client renders its own iframe
        // with no remote src, so these CSP/loading diagnostics don't apply.
        const containerEl = document.querySelector(`#${containerId}`);
        if (!import.meta.env.DEV && containerEl) {
          // Give the SDK a moment to inject its iframe
          setTimeout(() => {
            const iframe = containerEl.querySelector("iframe");
            if (!iframe) {
              console.warn(
                "[Access Dev] SDK initialized but no iframe found in container. " +
                  "The travel client may have failed to render. Check CSP headers " +
                  "and ensure frame-src allows *.accessdevelopment.com and *.adcrws.com."
              );
              return;
            }

            // Listen for iframe load/error
            iframe.addEventListener("load", () => {
              if (import.meta.env.DEV) console.info("[Access Dev] Iframe loaded successfully.");
            });

            iframe.addEventListener("error", () => {
              console.error(
                "[Access Dev] Iframe failed to load. The booking.accessdevelopment.com " +
                  "domain may be unreachable or blocked by CSP/network policy."
              );
            });

            // Check if the iframe src looks valid
            const src = iframe.getAttribute("src") || "";
            if (!src) {
              console.warn(
                "[Access Dev] Iframe has no src attribute — SDK may not have configured it correctly."
              );
            } else if (
              !src.includes("accessdevelopment.com") &&
              !src.includes("adcrws.com")
            ) {
              console.warn(
                `[Access Dev] Iframe src "${src}" does not point to an Access Development domain.`
              );
            }
          }, 2000);
        }

        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to initialize";
        const isCsp =
          err instanceof Error &&
          (err.message.includes("Security") ||
            err.message.includes("CSP") ||
            err.message.includes("frame"));

        setError({ message, isCsp });
        setStatus("error");
        console.error("Travel Client error:", err);

        return false;
      }
    },
    [containerId, width, height, language, navigateTo, markSessionRestartRequired]
  );

  const refreshSession = useCallback(async (): Promise<boolean> => {
    resetTokenState();

    if (typeof window === "undefined") {
      return false;
    }

    window.location.reload();
    return true;
  }, [resetTokenState]);

  // Initialize SDK
  useEffect(() => {
    if (isInitialized.current || !user) return;

    const startSdk = async () => {
      const token = await fetchToken(user);

      if (!token) {
        setError({
          message:
            "Failed to get Access token, refresh the page and try again",
        });
        setStatus("error");
        return;
      }

      await initializeSdk(token);
    };

    startSdk();
  }, [user, fetchToken, initializeSdk]);

  // Handle CSP violation events
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleCspError = (event: SecurityPolicyViolationEvent) => {
      if (isTravelClientCspViolation(event)) {
        setError({
          message: `Content Security Policy violation: ${event.blockedURI} was blocked.`,
          isCsp: true,
        });
        setStatus("error");
      }
    };

    document.addEventListener("securitypolicyviolation", handleCspError);

    return () => {
      document.removeEventListener("securitypolicyviolation", handleCspError);
    };
  }, []);

  // Save/restore scroll position for iframe interactions
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleScroll = () => {
      lastScrollPosition.current = window.scrollY;
    };

    const handleFocus = () => {
      window.scrollTo({
        top: lastScrollPosition.current,
        behavior: "auto",
      });
    };

    const containerEl = containerRef.current;

    const findIframeAndAttachEvents = () => {
      if (!containerEl) return;
      const iframe = containerEl.querySelector("iframe");
      if (iframe) {
        iframe.addEventListener("blur", handleFocus);
      }
    };

    window.addEventListener("scroll", handleScroll);
    const timeoutId = setTimeout(findIframeAndAttachEvents, 1000);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
      if (containerEl) {
        const iframe = containerEl.querySelector("iframe");
        if (iframe) {
          iframe.removeEventListener("blur", handleFocus);
        }
      }
    };
  }, [containerRef]);

  const updateLanguage = (newLanguage: string) => {
    if (
      typeof window === "undefined" ||
      !window.travelClient ||
      !isInitialized.current
    )
      return;
    window.travelClient.updateLanguage(newLanguage);
    onLanguageChangeRef.current?.(newLanguage);
  };

  const navigateToView = (navigation: NavigateTo) => {
    if (
      typeof window === "undefined" ||
      !window.travelClient ||
      !isInitialized.current
    )
      return;
    window.travelClient.navigateTo(navigation);
    onNavigateRef.current?.(navigation);
  };

  return {
    status,
    error,
    sdkError,
    containerRef,
    updateLanguage,
    navigateToView,
    refreshSession,
  };
};
